/**
 * A single 汉字 shown being written, one stroke at a time.
 *
 * A 词典 entry whose headword is one character carries a figure saying how the
 * character is written. `partials/cidian-bishun.html` renders the
 * character in the web font and this replaces it with the strokes themselves,
 * drawn in writing order and coloured by the part each belongs to, out of the
 * file `src/assets/stroke-data.ts` published for that character.
 *
 * **It arrives written, and is replayed on a press.** Nothing moves until a
 * reader asks for it. A figure that animated on load would have played to an
 * empty screen for most readers, since the block sits under the meanings, and
 * one that animated on scroll would need an observer this module would rather
 * not carry. Starting complete also means a reader who wants the shape gets it
 * with no press at all, and a reader who has asked their browser for less
 * motion is never given any they did not ask for.
 *
 * The rest of the feature is four modules beside this one.
 * `bishun-drawing.ts` builds the SVG, `bishun-median.ts` measures the line each
 * stroke is written along, `bishun-playback.ts` writes them out in order and
 * `bishun-legend.ts` names the parts under the figure. What is here is the
 * figure itself, the fetch behind it and the button.
 */

import {
  type BrushStroke,
  type StrokeData,
  strokeDrawing,
} from "./bishun-drawing.js";
import { writeLegend } from "./bishun-legend.js";
import { playStrokes, setBrush } from "./bishun-playback.js";

/** Where the stroke files are, and which character and parts this figure is. */
const figureSelector = ".cidian-bishun";

/** The character drawn in the web font, which the strokes replace. */
const glyphSelector = ".cidian-bishun-glyph";

/** The button that replays the writing. */
const playSelector = ".cidian-bishun-play";

/** Where the parts and their stroke counts are listed. */
const legendSelector = ".cidian-bishun-legend";

/**
 * One `data-` attribute of the figure, or the empty string where it has none.
 *
 * Through one reader rather than four, because three of the four are always
 * written and the fourth is left out for the 279 characters whose parts the
 * block cannot show. Four fallbacks would be three that nothing can reach.
 */
function attribute(figure: HTMLElement, name: string): string {
  return figure.dataset[name] ?? "";
}

/** Everything one figure needs, read off the markup the template wrote. */
function figureData(figure: HTMLElement): {
  readonly url: string;
  readonly character: string;
  readonly strokeParts: string;
  readonly parts: readonly string[];
} {
  const parts = attribute(figure, "cbParts");

  return {
    url: attribute(figure, "cbBishun"),
    character: attribute(figure, "cbCharacter"),
    strokeParts: attribute(figure, "cbStrokeParts"),
    parts: parts === "" ? [] : parts.split(" "),
  };
}

/**
 * Draw one figure, and wire its button.
 *
 * The fetch is the only thing that can fail, and a failure leaves the page
 * exactly as it was rendered: the character in the web font and no button. That
 * is the same page the 3,673 characters with no strokes get, which is the state
 * this block is already built to be readable in.
 */
async function startFigure(figure: HTMLElement): Promise<void> {
  const { url, character, strokeParts, parts } = figureData(figure);
  const glyph = figure.querySelector(glyphSelector);

  if (url === "" || character === "" || glyph === null) {
    return;
  }

  const response = await fetch(`${url}/${encodeURIComponent(character)}.json`);

  if (!response.ok) {
    return;
  }

  const { drawing, strokes } = strokeDrawing(
    figure.ownerDocument,
    (await response.json()) as StrokeData,
    strokeParts,
  );

  for (const stroke of strokes) {
    setBrush(stroke, true);
  }

  glyph.replaceWith(drawing);

  const legend = figure.querySelector(legendSelector);

  if (legend !== null && parts.length > 0) {
    writeLegend(legend, strokeParts, parts);
  }

  wirePlayButton(figure, strokes);
}

/** Show the replay button, and make it replay. */
function wirePlayButton(
  figure: ParentNode,
  strokes: readonly BrushStroke[],
): void {
  const play = figure.querySelector<HTMLButtonElement>(playSelector);

  if (play === null) {
    return;
  }

  let stop: (() => void) | undefined;

  play.addEventListener("click", () => {
    stop?.();
    stop = playStrokes(strokes);
  });

  play.hidden = false;
}

/**
 * Draw every writing figure under `root`.
 *
 * One per page in practice, since the block is only rendered for a headword of
 * one character, and written as a sweep anyway so that nothing depends on that.
 */
export function startBishun(root: ParentNode): void {
  for (const figure of root.querySelectorAll<HTMLElement>(figureSelector)) {
    void startFigure(figure);
  }
}
